<template>
  <Modal ref="modalRef" @ok="updateScope" @shown="onShown" @secondary-action="updateScope">
    <template #title>Select common ancestors</template>
    <p>
      Most people start by creating a group with <b>their parents</b> as the common ancestors.
    </p>
    <ul class="list-unstyled">
      <li v-for="vfId in candidateFamilyIds" :key="vfId">
        <FamilyCard :family-id="vfId" select-mode @click="onSelected(vfId)"/>
      </li>
    </ul>
    <template #okText>
      <template v-if="candidateFamilyIds.length == 1">
        Use my parents
      </template>
      <template v-else>
        Use selected parents
      </template>
    </template>
    <template #secondaryText>Select someone else</template>
  </Modal>
</template>

<style lang="scss" scoped>
.family-card {
  margin-left: 2rem;
}
</style>

<script lang="ts" setup>
import { ref } from 'vue'
import { useFamilyGroupStore } from '@/gp/FamilyGroupStore'
import { PatchChange } from '@/util/Api'
import { hideModalAsync } from '@/util/AppUtil'
import Modal from '@/util/Modal.vue'
import FamilyCard from '../FamilyCard.vue'
import { AsyncUtil } from '@/util/AsyncUtil'

const props = defineProps<{
  familyGroupId: string
  candidateFamilyIds: string[]
}>()

const emit = defineEmits([
  'selectNone'
])

defineExpose({
  open: () => modalRef.value?.open(),
})

const selectedFamilyIdRef = ref<string>()
const modalRef = ref<InstanceType<typeof Modal>>()

const familyGroupStore = useFamilyGroupStore()

function onShown() {
  console.log(`FamilyGroupScopeModal: shown with candidates ${props.candidateFamilyIds}`)
  selectedFamilyIdRef.value = undefined
  if (props.candidateFamilyIds.length == 1) {
    selectedFamilyIdRef.value = props.candidateFamilyIds[0]
  }
}

function onSelected(familyId: string) {
  console.log(`FamilyGroupScopeModal: selected ${familyId}`)
  selectedFamilyIdRef.value = familyId
}

async function updateScope() {
  console.log(`FamilyGroupScopeModal: update scope to ${selectedFamilyIdRef.value}`)
  if (selectedFamilyIdRef.value) {
    const change = new PatchChange('/scopeFamilyId', selectedFamilyIdRef.value)
    const update = familyGroupStore.patchAsync(props.familyGroupId, [change])
    await AsyncUtil.atLeast(500, update)
    modalRef.value?.close()
  }
  else {
    await hideModalAsync(modalRef.value?.$el)
    emit('selectNone')
  }
}
</script>
